import { useState, useCallback, useRef } from 'react';
import type { Message, ChatStep, ActionButton } from '@/types/chat';

const TYPING_DELAY = 950;
const TYPING_DELAY_LONG = 1500;

function makeId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function botMsg(text: string, buttons?: ActionButton[]): Message {
  return {
    id: makeId(),
    type: 'bot',
    text,
    timestamp: new Date(),
    actionButtons: buttons,
    buttonsUsed: false,
  };
}

function userMsg(text: string): Message {
  return {
    id: makeId(),
    type: 'user',
    text,
    timestamp: new Date(),
  };
}

const INTEREST_BTNS: ActionButton[] = [
  { id: 'btn_atacado', label: 'Atacado', type: 'flow', value: 'Atacado', variant: 'primary' },
  { id: 'btn_varejo', label: 'Varejo', type: 'flow', value: 'Varejo', variant: 'primary' },
];

const RETAIL_BTNS: ActionButton[] = [
  {
    id: 'btn_ml',
    label: 'Mercado Livre',
    type: 'link',
    href: 'https://www.mercadolivre.com.br/loja/bozzetti-store?utm_medium=social&utm_source=heylink.me',
    variant: 'mercadolivre',
  },
  {
    id: 'btn_shopee',
    label: 'Shopee',
    type: 'link',
    href: 'https://shopee.com.br/bozzettistore702?categoryId=100017&entryPoint=ShopByPDP&itemId=58207761624&utm_medium=social&utm_source=heylink.me',
    variant: 'shopee',
  },
];

const WHOLESALE_BTNS: ActionButton[] = [
  {
    id: 'btn_whatsapp',
    label: 'Falar no WhatsApp',
    type: 'link',
    href: 'https://wa.me/5511974876232',
    variant: 'whatsapp',
  },
];

const INSTAGRAM_BTNS: ActionButton[] = [
  {
    id: 'btn_instagram',
    label: 'Seguir no Instagram',
    type: 'link',
    href: 'https://www.instagram.com/bozzettistore/',
    variant: 'instagram',
  },
];

const RESTART_BTNS: ActionButton[] = [
  { id: 'btn_sim', label: 'Sim', type: 'flow', value: 'sim', variant: 'primary' },
  { id: 'btn_nao', label: 'Não', type: 'flow', value: 'nao', variant: 'primary' },
];

type BotQueueItem = {
  text: string;
  buttons?: ActionButton[];
  afterStep?: ChatStep;
  delay: number;
};

export function useChatFlow(
  onLeadCaptured: (name: string, email: string, phone: string, interest: 'Atacado' | 'Varejo') => void | Promise<void>
) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [step, setStep] = useState<ChatStep>('GREETING');
  const [isTyping, setIsTyping] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);

  const nameRef = useRef('');
  const emailRef = useRef('');
  const phoneRef = useRef('');
  const startedRef = useRef(false);
  const stepRef = useRef<ChatStep>('GREETING');

  const push = useCallback((msg: Message) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const markUsed = useCallback((msgId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, buttonsUsed: true } : m))
    );
  }, []);

  const runQueue = useCallback(
    (queue: BotQueueItem[], startDelay = 0) => {
      let cumulativeDelay = startDelay;

      queue.forEach((item) => {
        cumulativeDelay += item.delay;
        const capturedDelay = cumulativeDelay;

        setTimeout(() => {
          setIsTyping(true);

          setTimeout(() => {
            setIsTyping(false);
            push(botMsg(item.text, item.buttons));

            if (item.afterStep) {
              setStep(item.afterStep);
              stepRef.current = item.afterStep;
            }
          }, TYPING_DELAY);
        }, capturedDelay);
      });
    },
    [push]
  );

  const startConversation = useCallback(() => {
    if (startedRef.current) return;

    startedRef.current = true;
    setMessages([]);
    setInputDisabled(false);
    setStep('WAITING_NAME');
    stepRef.current = 'WAITING_NAME';
    nameRef.current = '';
    emailRef.current = '';
    phoneRef.current = '';

    runQueue([
      {
        text: 'Olá, seja bem-vinda à Bozzetti Store.\nQual o seu nome?',
        afterStep: 'WAITING_NAME',
        delay: 400,
      },
    ]);
  }, [runQueue]);

  const restartConversation = useCallback(() => {
    startedRef.current = false;
    setMessages([]);
    nameRef.current = '';
    emailRef.current = '';
    phoneRef.current = '';

    setTimeout(() => {
      startedRef.current = true;
      setStep('WAITING_NAME');
      stepRef.current = 'WAITING_NAME';
      setInputDisabled(false);

      runQueue([
        {
          text: 'Olá, seja bem-vinda à Bozzetti Store.\nQual o seu nome?',
          afterStep: 'WAITING_NAME',
          delay: 300,
        },
      ]);
    }, 200);
  }, [runQueue]);

  const handleUserTextInput = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      const currentStep = stepRef.current;

      if (currentStep === 'WAITING_NAME') {
        nameRef.current = text.trim();
        push(userMsg(text));
        setStep('WAITING_EMAIL');
        stepRef.current = 'WAITING_EMAIL';

        runQueue([
          {
            text: 'Qual o seu e-mail?',
            afterStep: 'WAITING_EMAIL',
            delay: 200,
          },
        ]);
      } else if (currentStep === 'WAITING_EMAIL') {
        emailRef.current = text.trim();
        push(userMsg(text));
        setStep('WAITING_PHONE');
        stepRef.current = 'WAITING_PHONE';

        runQueue([
          {
            text: 'Qual o seu telefone?',
            afterStep: 'WAITING_PHONE',
            delay: 200,
          },
        ]);
      } else if (currentStep === 'WAITING_PHONE') {
        phoneRef.current = text.trim();
        push(userMsg(text));
        setInputDisabled(true);
        setStep('WAITING_INTEREST');
        stepRef.current = 'WAITING_INTEREST';

        runQueue([
          {
            text: 'Você tem interesse nas peças no atacado ou no varejo?',
            buttons: INTEREST_BTNS,
            afterStep: 'WAITING_INTEREST',
            delay: 200,
          },
        ]);
      }
    },
    [push, runQueue]
  );

  const handleFlowButton = useCallback(
    async (value: string, parentMsgId: string) => { // Tornando a função assíncrona
      markUsed(parentMsgId);
      const currentStep = stepRef.current;

      if (currentStep === 'WAITING_INTEREST') {
        push(userMsg(value));
        setInputDisabled(true);

        const interest = value as 'Atacado' | 'Varejo';

        // LOG de Depuração: Veja se isso aparece no seu console (F12)
        console.log('Tentando salvar lead no Firestore...', {
          name: nameRef.current,
          email: emailRef.current,
          phone: phoneRef.current,
          interest,
        });

        // CORREÇÃO: Usar await para garantir que o lead suba antes de seguir o fluxo do chat
        try {
          await onLeadCaptured(nameRef.current, emailRef.current, phoneRef.current, interest);
          console.log('Lead salvo com sucesso no Firebase!');
        } catch (error) {
          console.error('ERRO CRÍTICO AO SALVAR LEAD:', error);
          // Opcional: Tentar salvar novamente ou alertar o sistema
        }

        if (interest === 'Varejo') {
          runQueue([
            {
              text: 'Para compras no varejo, acesse nossas lojas oficiais no Mercado Livre ou na Shopee.',
              buttons: RETAIL_BTNS,
              afterStep: 'RETAIL_LINKS',
              delay: 200,
            },
            {
              text: 'Obrigada pelo seu contato 💖\nNão se esqueça de seguir a nossa página no Instagram para acompanhar todas as novidades!',
              buttons: INSTAGRAM_BTNS,
              delay: TYPING_DELAY_LONG + 600,
            },
            {
              text: 'Deseja iniciar uma nova conversa?',
              buttons: RESTART_BTNS,
              afterStep: 'RESTART_PROMPT',
              delay: TYPING_DELAY_LONG + 400,
            },
          ]);
        } else {
          runQueue([
            {
              text: 'O pedido mínimo para o atacado é de 10 peças, podendo ser peças variadas. Realizamos vendas para CPF ou CNPJ e envio para todo o Brasil. Para vendas no atacado, entre em contato conosco através do WhatsApp 11974876232. Ou clique no botão abaixo.',
              buttons: WHOLESALE_BTNS,
              afterStep: 'WHOLESALE_INFO',
              delay: 200,
            },
            {
              text: 'Obrigada pelo seu contato 💖\nNão se esqueça de seguir a nossa página no Instagram para acompanhar todas as novidades!',
              buttons: INSTAGRAM_BTNS,
              delay: TYPING_DELAY_LONG + 600,
            },
            {
              text: 'Deseja iniciar uma nova conversa?',
              buttons: RESTART_BTNS,
              afterStep: 'RESTART_PROMPT',
              delay: TYPING_DELAY_LONG + 400,
            },
          ]);
        }
      } else if (currentStep === 'RESTART_PROMPT') {
        push(userMsg(value));

        if (value === 'sim') {
          setTimeout(() => restartConversation(), 400);
        } else {
          setStep('END');
          stepRef.current = 'END';

          runQueue([
            {
              text: 'Foi um prazer te atender! 💖',
              afterStep: 'END',
              delay: 200,
            },
          ]);

          setInputDisabled(true);
        }
      }
    },
    [push, markUsed, onLeadCaptured, runQueue, restartConversation]
  );

  return {
    messages,
    step,
    isTyping,
    inputDisabled,
    startConversation,
    handleUserTextInput,
    handleFlowButton,
  };
}